import React, { useEffect, useState } from 'react'
import { getNameByField } from './Utils'
import { IconButton, InputAdornment, OutlinedInput, Stack } from '@mui/material'
import { red } from '@mui/material/colors'
import { AddCircle, Cancel, Save } from '@mui/icons-material'
import CircularProgress from '@mui/material/CircularProgress'
import useResponsive from 'hooks/useResponsive'
import { useTheme } from '@mui/material/styles'
import { Setup } from '../../../models/interfaces/setup.interface'
import { WorkoutTypeEnum } from '../../../models/enums/workout-type.enum'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { modalAddFieldsData } from '../../../redux/reducers/modal-add-fields/modal-add-fields.selectors'
import {
  saveSetup,
  updateSetupTrainingWorkout
} from '../../../redux/reducers/training-workout/training-workout.actions'
import { showMessage } from '../../../redux/reducers/snackbar/snackbar.actions'
import { showModalAddFields } from '../../../redux/reducers/modal-add-fields/modal-add-fields.actions'
import { selectedTrainingWorkoutType } from '../../../redux/reducers/training-workout/training-workout.selectors'

type Props = {
  setup: Setup[]
  trainingWorkoutId?: string
  updateFieldChanged?: (value: any, item: any) => void
  onClose: () => void
  isGrouped?: boolean
  workoutType: WorkoutTypeEnum
}

export const SetupForm = ({
  setup,
  trainingWorkoutId,
  onClose,
  isGrouped = false,
  workoutType = WorkoutTypeEnum.BODYBUILDING
}: Props) => {
  const dispatch = useAppDispatch()
  const [isSaving, setIsSaving] = useState(false)
  const [newSetup, setNewSetup] = useState<Setup[]>(setup)
  const trainingWorkoutType = useAppSelector(selectedTrainingWorkoutType)
  const theme = useTheme()
  const mdUp = useResponsive('up', 'md')
  const data = useAppSelector(modalAddFieldsData)

  useEffect(() => {
    if (data && data.id === trainingWorkoutId) {
      setNewSetup(setup)
    }
  }, [data])

  const handleSaveSetup = async () => {
    try {
      setIsSaving(false)
      dispatch(saveSetup({ setups: newSetup, id: String(trainingWorkoutId), isGrouped }))
      onClose()
      await dispatch(updateSetupTrainingWorkout({
        payload: { setup: newSetup },
        trainingWorkoutId: String(trainingWorkoutId),
        type: trainingWorkoutType
      }))
      dispatch(showMessage({ message: 'Salvo com sucesso' }))
    } catch (e) {
      dispatch(showMessage({ message: 'Erro ao salvar registro', type: 'error' }))
    } finally {
      setIsSaving(false)
    }
  }

  const handleOnChange = (value: any, item: any) => {
    const changed = newSetup?.map((obj: any) => {
      if (obj.field === item.field) return { ...obj, value }
      return obj
    })
    setNewSetup(changed)
  }

  const handleRemoveField = async (field: string) => {
    try {
      setIsSaving(false)
      const filtered = newSetup.filter(item => item.field !== field)
      await dispatch(updateSetupTrainingWorkout({
        payload: { setup: filtered },
        trainingWorkoutId: String(trainingWorkoutId),
        type: trainingWorkoutType
      }))
      dispatch(showMessage({ message: 'Salvo com sucesso' }))
      setNewSetup(filtered)
    } catch (e) {
      dispatch(showMessage({ message: 'Erro ao excluir registro', type: 'error' }))
    } finally {
      setIsSaving(false)
    }
  }

  const handleShowAddModal = () => {
    const payload = { data: { setup: newSetup, id: String(trainingWorkoutId), workoutType }, isVisible: true }
    dispatch(showModalAddFields(payload))
  }

  return (
    <Stack p={1.5} >
      <Stack direction={(mdUp ? 'row' : 'column')} alignItems="stretch">
        {newSetup?.map((item: Setup) => {
          return (
            <Stack key={item.field} mr={2} justifyContent="end">
              <Stack>
                <span style={{
                  color: theme.palette.grey[300],
                  marginLeft: 1,
                  marginBottom: 2,
                  fontSize: 14,
                  paddingBottom: 8,
                  paddingLeft: 2,
                  paddingRight: 2
                }}>
                  {getNameByField(item.field)}
                </span>
              </Stack>
              <Stack pb={1.5}>
                <OutlinedInput
                  style={{ backgroundColor: theme.palette.grey[900], borderColor: theme.palette.grey[900] }}
                  size="small"
                  placeholder={getNameByField(item.field)}
                  value={item.value}
                  onChange={e => handleOnChange(e.target.value, item)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        title="Excluir campo"
                        arial-label={getNameByField(item.field)}
                        onClick={async () => await handleRemoveField(item.field)}
                        style={{ marginTop: -40, marginRight: -25, zIndex: 1 }}
                      >
                        <Cancel sx={{ color: red[900] }}/>
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Stack>
            </Stack>
          )
        })}
        <Stack mt={2} direction="row" alignItems="center" justifyItems="center" justifyContent="center">
          <Stack>
            <IconButton color="default" onClick={handleSaveSetup} disabled={isSaving}>
              {isSaving ? <CircularProgress size={18} /> : <Save/>}
            </IconButton>
          </Stack>
          <Stack>
            <IconButton onClick={handleShowAddModal}>
              <AddCircle></AddCircle>
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}
